import { useEffect, useState } from 'react';
import { CurrencyCode } from '../../config/currency';
import { FinancialStatementType } from '../../types/reports';
import { QRCodeSVG } from 'qrcode.react';
import { useCurrency } from '../../hooks/useCurrency';

interface PrintableReportProps {
  data: any;
  type: FinancialStatementType;
  title: string;
  organization: any;
  generatedBy: string;
  currency: CurrencyCode;
  date: string;
  isAudited?: boolean;
}

export function PrintableReport({
  data,
  type,
  title,
  organization,
  generatedBy,
  currency,
  date,
  isAudited = false
}: PrintableReportProps) {
  const [qrValue, setQrValue] = useState('');
  const { format } = useCurrency();
  
  // Générer une valeur pour le QR code d'authentification
  useEffect(() => {
    // Créer une chaîne unique avec des informations d'authentification
    const authString = JSON.stringify({
      org: organization.registrationNumber,
      type: type,
      date: new Date().toISOString(),
      generator: generatedBy,
      hash: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    });
    
    // Encoder en base64 pour un QR code plus compact
    const encodedAuth = btoa(authString);
    setQrValue(`https://verify.wanzo.cd/auth/${encodedAuth}`);
  }, [organization, type, generatedBy]);

  // Formatter pour les montants
  const formatAmount = (amount: number | undefined): string => {
    if (amount === undefined) return '-';
    return format(amount, currency);
  };

  // Helpers
  const getArray = (obj: any, key: string): any[] => {
    const val = obj?.[key];
    return Array.isArray(val) ? val : [];
  };
  const nonEmpty = (items: any[]): any[] => items.filter((i: any) => i?.code || i?.label);
  const wrapItem = (item: any) => item ? [item] : [];

  // ---- Normalisation : accepte le format brut backend (actif/passif) et le format adapté (fixedAssets/…) ----
  const normalizeBalanceData = () => {
    const isRawApi = data?.actif !== undefined || data?.passif !== undefined;
    if (!isRawApi) {
      return {
        fixedAssets: data?.fixedAssets,
        currentAssets: data?.currentAssets,
        treasuryAssets: data?.treasuryAssets,
        equity: data?.equity,
        financialDebts: data?.financialDebts,
        currentLiabilities: data?.currentLiabilities,
        treasuryLiabilities: data?.treasuryLiabilities,
        totalAssets: data?.totalAssets,
        totalLiabilities: data?.totalLiabilities,
      };
    }
    const immo = data.actif?.immobilisations ?? {};
    const circ = data.actif?.actifCirculant ?? {};
    const tresoActif = data.actif?.tresorerieActif;
    const caps = data.passif?.capitauxPropresNets ?? {};
    const dettesFin = data.passif?.dettesFinancieresLT ?? {};
    const passifCirc = data.passif?.passifCirculant ?? {};

    return {
      fixedAssets: {
        intangibleAssets: [...wrapItem(immo.chargesImmobilisees), ...wrapItem(immo.incorporelles)],
        tangibleAssets: wrapItem(immo.corporelles),
        financialAssets: [...wrapItem(immo.financieres), ...wrapItem(immo.avancesAcomptes)],
        total: immo.total,
      },
      currentAssets: {
        inventory: wrapItem(circ.stocks),
        receivables: wrapItem(circ.creancesClients),
        other: [circ.creancesFiscales, circ.autresCreances, circ.regularisationActif].filter(Boolean),
        total: circ.total,
      },
      treasuryAssets: {
        banks: tresoActif ? [tresoActif] : [],
        total: tresoActif ?? { code: 'AT', label: 'Trésorerie Actif', brut: 0, amort: 0, net: 0 },
      },
      equity: {
        capital: wrapItem(caps.capital),
        reserves: [caps.reserves, caps.subventionsInvestissement, caps.provisionsReglementees].filter(Boolean),
        result: [caps.reportANouveau, caps.resultat].filter(Boolean),
        total: caps.total,
      },
      financialDebts: {
        loans: wrapItem(dettesFin.emprunts),
        leasing: wrapItem(dettesFin.dettesCreditBail),
        other: wrapItem(dettesFin.dettesParticipations),
        total: dettesFin.total,
      },
      currentLiabilities: {
        suppliers: wrapItem(passifCirc.fournisseurs),
        fiscal: wrapItem(passifCirc.dettesFiscales),
        other: [passifCirc.autresDettes, passifCirc.regularisationPassif].filter(Boolean),
        total: passifCirc.total,
      },
      treasuryLiabilities: {
        banks: data.passif?.tresoreriePassif ? [data.passif.tresoreriePassif] : [],
        total: data.passif?.tresoreriePassif ?? { code: 'PT', label: 'Trésorerie Passif', net: 0 },
      },
      totalAssets: data.actif?.grandTotal,
      totalLiabilities: data.passif?.grandTotal,
    };
  };

  const normalizeIncomeData = () => {
    const isRawApi = data?.produitsExploitation !== undefined || data?.chargesExploitation !== undefined;
    if (!isRawApi) {
      return {
        operatingIncome: data?.operatingIncome,
        operatingExpenses: data?.operatingExpenses,
        financialIncome: data?.financialIncome,
        financialExpenses: data?.financialExpenses,
        netResult: data?.netResult,
      };
    }
    return {
      operatingIncome: {
        sales: wrapItem(data.produitsExploitation?.ventesMarchandises),
        other: [data.produitsExploitation?.productionVendue, data.produitsExploitation?.autresProduits].filter(Boolean),
        total: data.produitsExploitation?.total ?? { current: 0, previous: 0 },
      },
      operatingExpenses: {
        purchases: wrapItem(data.chargesExploitation?.achatsMarchandises),
        other: [data.chargesExploitation?.transports, data.chargesExploitation?.servicesExterieurs,
                data.chargesExploitation?.impots, data.chargesExploitation?.chargesPersonnel,
                data.chargesExploitation?.dotationsAmort].filter(Boolean),
        total: data.chargesExploitation?.total ?? { current: 0, previous: 0 },
      },
      financialIncome: data.produitsFinanciers ? { items: wrapItem(data.produitsFinanciers), total: data.produitsFinanciers.total } : undefined,
      financialExpenses: data.chargesFinancieres ? { items: wrapItem(data.chargesFinancieres), total: data.chargesFinancieres.total } : undefined,
      netResult: data.resultatNet ?? data.netResult,
    };
  };

  const normalizeCashflowData = () => {
    const isRawApi = data?.fluxExploitation !== undefined;
    if (!isRawApi) {
      return {
        operatingActivities: data?.operatingActivities,
        investingActivities: data?.investingActivities,
        financingActivities: data?.financingActivities,
        netCashChange: data?.netCashChange,
      };
    }
    return {
      operatingActivities: {
        netResult: data.fluxExploitation?.resultatNet ?? { code: 'FA', label: 'Résultat net', current: 0, previous: 0 },
        items: [data.fluxExploitation?.dotations, data.fluxExploitation?.variationBFR].filter(Boolean),
        total: data.fluxExploitation?.total,
      },
      investingActivities: data.fluxInvestissement ? {
        items: [data.fluxInvestissement.acquisitions, data.fluxInvestissement.cessions].filter(Boolean),
        total: data.fluxInvestissement.total,
      } : undefined,
      financingActivities: data.fluxFinancement ? {
        items: [data.fluxFinancement.emprunts, data.fluxFinancement.remboursements, data.fluxFinancement.dividendes].filter(Boolean),
        total: data.fluxFinancement.total,
      } : undefined,
      netCashChange: data.variationTresorerie ?? data.netCashChange,
    };
  };

  const renderBalanceSheet = () => {
    const n = normalizeBalanceData();
    if (!n.fixedAssets && !n.equity && !n.totalAssets) {
      return <div>Données insuffisantes pour le bilan</div>;
    }

    return (
      <>
        {/* Actif */}
        <div className="mb-8">
          <h3 className="text-lg font-bold mb-4 text-black">ACTIF</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left">Code</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Libellé</th>
                <th className="border border-gray-300 px-4 py-2 text-right">Brut</th>
                <th className="border border-gray-300 px-4 py-2 text-right">Amort/Prov</th>
                <th className="border border-gray-300 px-4 py-2 text-right">Net</th>
                <th className="border border-gray-300 px-4 py-2 text-right">Net N-1</th>
              </tr>
            </thead>
            <tbody>
              {/* Actif immobilisé */}
              <tr className="bg-gray-50">
                <td colSpan={6} className="border border-gray-300 px-4 py-2 font-medium">Actif immobilisé</td>
              </tr>
              {nonEmpty(getArray(n.fixedAssets, 'intangibleAssets')).map((item: any, index: number) => (
                <tr key={`intangible-${index}`}>
                  <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.brut)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.amort)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                </tr>
              ))}
              {nonEmpty(getArray(n.fixedAssets, 'tangibleAssets')).map((item: any, index: number) => (
                <tr key={`tangible-${index}`}>
                  <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.brut)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.amort)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                </tr>
              ))}
              {nonEmpty(getArray(n.fixedAssets, 'financialAssets')).map((item: any, index: number) => (
                <tr key={`financial-${index}`}>
                  <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.brut)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.amort)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                </tr>
              ))}
              <tr className="bg-gray-100">
                <td colSpan={2} className="border border-gray-300 px-4 py-2 font-medium">Total Actif immobilisé</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.fixedAssets?.total?.brut)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.fixedAssets?.total?.amort)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.fixedAssets?.total?.net)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.fixedAssets?.total?.netN1)}</td>
              </tr>

              {/* Actif circulant */}
              <tr className="bg-gray-50">
                <td colSpan={6} className="border border-gray-300 px-4 py-2 font-medium">Actif circulant</td>
              </tr>
              {nonEmpty(getArray(n.currentAssets, 'inventory')).map((item: any, index: number) => (
                <tr key={`inventory-${index}`}>
                  <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.brut)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.amort)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                </tr>
              ))}
              {nonEmpty(getArray(n.currentAssets, 'receivables')).map((item: any, index: number) => (
                <tr key={`receivable-${index}`}>
                  <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.brut)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.amort)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                </tr>
              ))}
              {nonEmpty(getArray(n.currentAssets, 'other')).map((item: any, index: number) => (
                <tr key={`other-ca-${index}`}>
                  <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.brut)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.amort)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                </tr>
              ))}
              <tr className="bg-gray-100">
                <td colSpan={2} className="border border-gray-300 px-4 py-2 font-medium">Total Actif circulant</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.currentAssets?.total?.brut)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.currentAssets?.total?.amort)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.currentAssets?.total?.net)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.currentAssets?.total?.netN1)}</td>
              </tr>

              {/* Trésorerie Actif */}
              {n.treasuryAssets && nonEmpty(getArray(n.treasuryAssets, 'banks')).length > 0 && (
                <>
                  <tr className="bg-gray-50">
                    <td colSpan={6} className="border border-gray-300 px-4 py-2 font-medium">Trésorerie Actif</td>
                  </tr>
                  {nonEmpty(getArray(n.treasuryAssets, 'banks')).map((item: any, index: number) => (
                    <tr key={`treasury-a-${index}`}>
                      <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                      <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                      <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.brut)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.amort)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100">
                    <td colSpan={2} className="border border-gray-300 px-4 py-2 font-medium">Total Trésorerie Actif</td>
                    <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.treasuryAssets.total?.brut)}</td>
                    <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.treasuryAssets.total?.amort)}</td>
                    <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.treasuryAssets.total?.net)}</td>
                    <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.treasuryAssets.total?.netN1)}</td>
                  </tr>
                </>
              )}

              {/* Total Actif */}
              <tr className="bg-gray-700 text-white">
                <td colSpan={2} className="border border-gray-300 px-4 py-2 font-bold">TOTAL ACTIF</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-bold">{formatAmount(n.totalAssets?.brut)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-bold">{formatAmount(n.totalAssets?.amort)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-bold">{formatAmount(n.totalAssets?.net)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-bold">{formatAmount(n.totalAssets?.netN1)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Passif */}
        <div>
          <h3 className="text-lg font-bold mb-4 text-black">PASSIF</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left">Code</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Libellé</th>
                <th className="border border-gray-300 px-4 py-2 text-right">Net</th>
                <th className="border border-gray-300 px-4 py-2 text-right">Net N-1</th>
              </tr>
            </thead>
            <tbody>
              {/* Capitaux propres */}
              <tr className="bg-gray-50">
                <td colSpan={4} className="border border-gray-300 px-4 py-2 font-medium">Capitaux propres</td>
              </tr>
              {nonEmpty(getArray(n.equity, 'capital')).map((item: any, index: number) => (
                <tr key={`capital-${index}`}>
                  <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                </tr>
              ))}
              {nonEmpty(getArray(n.equity, 'reserves')).map((item: any, index: number) => (
                <tr key={`reserve-${index}`}>
                  <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                </tr>
              ))}
              {nonEmpty(getArray(n.equity, 'result')).map((item: any, index: number) => (
                <tr key={`result-${index}`}>
                  <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                </tr>
              ))}
              <tr className="bg-gray-100">
                <td colSpan={2} className="border border-gray-300 px-4 py-2 font-medium">Total Capitaux propres</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.equity?.total?.net)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.equity?.total?.netN1)}</td>
              </tr>

              {/* Dettes financières */}
              {n.financialDebts && (
                <>
                  <tr className="bg-gray-50">
                    <td colSpan={4} className="border border-gray-300 px-4 py-2 font-medium">Dettes financières</td>
                  </tr>
                  {nonEmpty([...getArray(n.financialDebts, 'loans'), ...getArray(n.financialDebts, 'leasing'), ...getArray(n.financialDebts, 'other')]).map((item: any, index: number) => (
                    <tr key={`debt-${index}`}>
                      <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                      <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                      <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100">
                    <td colSpan={2} className="border border-gray-300 px-4 py-2 font-medium">Total Dettes financières</td>
                    <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.financialDebts.total?.net)}</td>
                    <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.financialDebts.total?.netN1)}</td>
                  </tr>
                </>
              )}

              {/* Passif circulant */}
              {n.currentLiabilities && (
                <>
                  <tr className="bg-gray-50">
                    <td colSpan={4} className="border border-gray-300 px-4 py-2 font-medium">Passif circulant</td>
                  </tr>
                  {nonEmpty([...getArray(n.currentLiabilities, 'suppliers'), ...getArray(n.currentLiabilities, 'fiscal'), ...getArray(n.currentLiabilities, 'other')]).map((item: any, index: number) => (
                    <tr key={`cl-${index}`}>
                      <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                      <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                      <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100">
                    <td colSpan={2} className="border border-gray-300 px-4 py-2 font-medium">Total Passif circulant</td>
                    <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.currentLiabilities.total?.net)}</td>
                    <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.currentLiabilities.total?.netN1)}</td>
                  </tr>
                </>
              )}

              {/* Trésorerie Passif */}
              {n.treasuryLiabilities && nonEmpty(getArray(n.treasuryLiabilities, 'banks')).length > 0 && (
                <>
                  <tr className="bg-gray-50">
                    <td colSpan={4} className="border border-gray-300 px-4 py-2 font-medium">Trésorerie Passif</td>
                  </tr>
                  {nonEmpty(getArray(n.treasuryLiabilities, 'banks')).map((item: any, index: number) => (
                    <tr key={`tp-${index}`}>
                      <td className="border border-gray-300 px-4 py-2">{item.code}</td>
                      <td className="border border-gray-300 px-4 py-2">{item.label}</td>
                      <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.net)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item.netN1)}</td>
                    </tr>
                  ))}
                </>
              )}

              {/* Total Passif */}
              <tr className="bg-gray-700 text-white">
                <td colSpan={2} className="border border-gray-300 px-4 py-2 font-bold">TOTAL PASSIF</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-bold">{formatAmount(n.totalLiabilities?.net)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-bold">{formatAmount(n.totalLiabilities?.netN1)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderIncomeStatement = () => {
    const n = normalizeIncomeData();
    if (!n.operatingIncome && !n.operatingExpenses && !n.netResult) {
      return <div>Données insuffisantes pour le compte de résultat</div>;
    }

    const renderIncomeRow = (item: any, key: string) => (
      <tr key={key}>
        <td className="border border-gray-300 px-4 py-2">{item?.code || ''}</td>
        <td className="border border-gray-300 px-4 py-2">{item?.label || ''}</td>
        <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item?.current ?? item?.net)}</td>
        <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item?.previous ?? item?.netN1)}</td>
        <td className="border border-gray-300 px-4 py-2 text-right">{item?.variation != null ? `${item.variation}%` : '-'}</td>
      </tr>
    );

    return (
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-4 py-2 text-left">Code</th>
            <th className="border border-gray-300 px-4 py-2 text-left">Libellé</th>
            <th className="border border-gray-300 px-4 py-2 text-right">N</th>
            <th className="border border-gray-300 px-4 py-2 text-right">N-1</th>
            <th className="border border-gray-300 px-4 py-2 text-right">Variation</th>
          </tr>
        </thead>
        <tbody>
          {/* Produits d'exploitation */}
          <tr className="bg-gray-50">
            <td colSpan={5} className="border border-gray-300 px-4 py-2 font-medium">Produits d&apos;exploitation</td>
          </tr>
          {nonEmpty(getArray(n.operatingIncome, 'sales')).map((item: any, idx: number) => renderIncomeRow(item, `sales-${idx}`))}
          {nonEmpty(getArray(n.operatingIncome, 'other')).map((item: any, idx: number) => renderIncomeRow(item, `oi-other-${idx}`))}
          <tr className="bg-gray-100">
            <td colSpan={2} className="border border-gray-300 px-4 py-2 font-medium">Total Produits d&apos;exploitation</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.operatingIncome?.total?.current)}</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.operatingIncome?.total?.previous)}</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-medium">{n.operatingIncome?.total?.variation != null ? `${n.operatingIncome.total.variation}%` : '-'}</td>
          </tr>

          {/* Charges d'exploitation */}
          <tr className="bg-gray-50">
            <td colSpan={5} className="border border-gray-300 px-4 py-2 font-medium">Charges d&apos;exploitation</td>
          </tr>
          {nonEmpty(getArray(n.operatingExpenses, 'purchases')).map((item: any, idx: number) => renderIncomeRow(item, `purch-${idx}`))}
          {nonEmpty(getArray(n.operatingExpenses, 'other')).map((item: any, idx: number) => renderIncomeRow(item, `oe-other-${idx}`))}
          <tr className="bg-gray-100">
            <td colSpan={2} className="border border-gray-300 px-4 py-2 font-medium">Total Charges d&apos;exploitation</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.operatingExpenses?.total?.current)}</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.operatingExpenses?.total?.previous)}</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-medium">{n.operatingExpenses?.total?.variation != null ? `${n.operatingExpenses.total.variation}%` : '-'}</td>
          </tr>

          {/* Résultat net */}
          <tr className="bg-gray-700 text-white">
            <td colSpan={2} className="border border-gray-300 px-4 py-2 font-bold">RÉSULTAT NET</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-bold">{formatAmount(n.netResult?.current ?? n.netResult?.net)}</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-bold">{formatAmount(n.netResult?.previous ?? n.netResult?.netN1)}</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-bold">{n.netResult?.variation != null ? `${n.netResult.variation}%` : '-'}</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderCashFlow = () => {
    const n = normalizeCashflowData();
    if (!n.operatingActivities && !n.netCashChange) {
      return <div>Données insuffisantes pour le tableau des flux</div>;
    }

    const renderCFRow = (item: any, key: string) => (
      <tr key={key}>
        <td className="border border-gray-300 px-4 py-2">{item?.code || ''}</td>
        <td className="border border-gray-300 px-4 py-2">{item?.label || ''}</td>
        <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item?.current ?? item?.net)}</td>
        <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(item?.previous ?? item?.netN1)}</td>
      </tr>
    );

    return (
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-4 py-2 text-left">Code</th>
            <th className="border border-gray-300 px-4 py-2 text-left">Libellé</th>
            <th className="border border-gray-300 px-4 py-2 text-right">N</th>
            <th className="border border-gray-300 px-4 py-2 text-right">N-1</th>
          </tr>
        </thead>
        <tbody>
          {/* Flux d'exploitation */}
          <tr className="bg-gray-50">
            <td colSpan={4} className="border border-gray-300 px-4 py-2 font-medium">Flux de trésorerie liés à l&apos;exploitation</td>
          </tr>
          {n.operatingActivities?.netResult && renderCFRow(n.operatingActivities.netResult, 'cf-nr')}
          {getArray(n.operatingActivities, 'items').map((item: any, idx: number) => renderCFRow(item, `cf-op-${idx}`))}
          {n.operatingActivities?.total && (
            <tr className="bg-gray-100">
              <td colSpan={2} className="border border-gray-300 px-4 py-2 font-medium">Total flux d&apos;exploitation</td>
              <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.operatingActivities.total?.current ?? n.operatingActivities.total?.net)}</td>
              <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.operatingActivities.total?.previous ?? n.operatingActivities.total?.netN1)}</td>
            </tr>
          )}

          {/* Flux d'investissement */}
          {n.investingActivities && (
            <>
              <tr className="bg-gray-50">
                <td colSpan={4} className="border border-gray-300 px-4 py-2 font-medium">Flux de trésorerie liés à l&apos;investissement</td>
              </tr>
              {getArray(n.investingActivities, 'items').map((item: any, idx: number) => renderCFRow(item, `cf-inv-${idx}`))}
              {n.investingActivities.total && (
                <tr className="bg-gray-100">
                  <td colSpan={2} className="border border-gray-300 px-4 py-2 font-medium">Total flux d&apos;investissement</td>
                  <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.investingActivities.total?.current ?? n.investingActivities.total?.net)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.investingActivities.total?.previous ?? n.investingActivities.total?.netN1)}</td>
                </tr>
              )}
            </>
          )}

          {/* Flux de financement */}
          {n.financingActivities && (
            <>
              <tr className="bg-gray-50">
                <td colSpan={4} className="border border-gray-300 px-4 py-2 font-medium">Flux de trésorerie liés au financement</td>
              </tr>
              {getArray(n.financingActivities, 'items').map((item: any, idx: number) => renderCFRow(item, `cf-fin-${idx}`))}
              {n.financingActivities.total && (
                <tr className="bg-gray-100">
                  <td colSpan={2} className="border border-gray-300 px-4 py-2 font-medium">Total flux de financement</td>
                  <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.financingActivities.total?.current ?? n.financingActivities.total?.net)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right font-medium">{formatAmount(n.financingActivities.total?.previous ?? n.financingActivities.total?.netN1)}</td>
                </tr>
              )}
            </>
          )}

          {/* Variation de trésorerie */}
          <tr className="bg-gray-700 text-white">
            <td colSpan={2} className="border border-gray-300 px-4 py-2 font-bold">VARIATION DE TRÉSORERIE</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-bold">{formatAmount(n.netCashChange?.current ?? n.netCashChange?.net)}</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-bold">{formatAmount(n.netCashChange?.previous ?? n.netCashChange?.netN1)}</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderTrialBalance = () => {
    // Le backend renvoie un tableau de comptes avec soldes
    const accounts = Array.isArray(data) ? data : (data?.accounts || data?.rows || []);
    if (!accounts.length) return <div>Aucune donnée de balance</div>;
    return (
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-4 py-2 text-left">Code</th>
            <th className="border border-gray-300 px-4 py-2 text-left">Libellé</th>
            <th className="border border-gray-300 px-4 py-2 text-right">Débit</th>
            <th className="border border-gray-300 px-4 py-2 text-right">Crédit</th>
            <th className="border border-gray-300 px-4 py-2 text-right">Solde Débiteur</th>
            <th className="border border-gray-300 px-4 py-2 text-right">Solde Créditeur</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((row: any, idx: number) => (
            <tr key={idx} className={row.isTotal ? 'bg-gray-700 text-white font-bold' : row.isClass ? 'bg-gray-100 font-medium' : ''}>
              <td className="border border-gray-300 px-4 py-2">{row.code || row.accountCode || ''}</td>
              <td className="border border-gray-300 px-4 py-2">{row.label || row.accountName || ''}</td>
              <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(row.debit ?? row.totalDebit)}</td>
              <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(row.credit ?? row.totalCredit)}</td>
              <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(row.debitBalance ?? row.soldeDebiteur)}</td>
              <td className="border border-gray-300 px-4 py-2 text-right">{formatAmount(row.creditBalance ?? row.soldeCrediteur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderGenericTable = () => {
    const rows = Array.isArray(data) ? data : (data?.entries || data?.rows || data?.lines || []);
    if (!rows.length) return <div>Aucune donnée disponible</div>;
    // Déterminer les colonnes depuis la première ligne
    const cols = Object.keys(rows[0]).filter(k => !k.startsWith('_') && k !== 'id');
    const colLabels: Record<string, string> = {
      date: 'Date', reference: 'Réf', description: 'Libellé', journalType: 'Journal',
      accountCode: 'Compte', accountName: 'Nom du compte', debit: 'Débit', credit: 'Crédit',
      totalDebit: 'Total Débit', totalCredit: 'Total Crédit', balance: 'Solde',
    };
    return (
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-100">
            {cols.map(c => (
              <th key={c} className="border border-gray-300 px-3 py-2 text-left">{colLabels[c] || c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row: any, idx: number) => (
            <tr key={idx}>
              {cols.map(c => (
                <td key={c} className={`border border-gray-300 px-3 py-1 ${typeof row[c] === 'number' ? 'text-right' : ''}`}>
                  {typeof row[c] === 'number' ? formatAmount(row[c]) : String(row[c] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="printable-report bg-white p-8">
      {/* En-tête du rapport avec les informations de l'organisation - réduit en hauteur */}
      <div className="mb-4">        <div className="flex justify-between items-center">
          {/* Logo et informations de l'organisation (alignés à gauche) */}
          <div className="flex items-center space-x-3 w-2/3">
            {/* Logo de l'entreprise */}
            {organization.logo ? (
              <img 
                src={organization.logo} 
                alt={`${organization.name} Logo`} 
                className="h-12 w-auto object-contain"
                style={{ maxWidth: '45px' }}
              />
            ) : (
              <div className="h-12 w-12 bg-gray-100 flex items-center justify-center border border-gray-300 rounded">
                <svg className="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="14" x="2" y="7" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
              </div>
            )}
            
            {/* Informations de l'organisation - Compactes */}
            <div>
              <h1 className="text-lg font-bold">{organization.name || 'Nom de l\'entreprise non défini'}</h1>
              <div className="flex flex-wrap text-xs space-x-2">
                {organization.registrationNumber && <span>RCCM: {organization.registrationNumber}</span>}
                {organization.taxId && <span>NINEA: {organization.taxId}</span>}
                {organization.phone && <span>Tél: {organization.phone}</span>}
              </div>
              <div className="text-xs text-gray-600">
                {organization.address && <span>{organization.address}</span>}
                {organization.email && <span className="ml-2">{organization.email}</span>}
              </div>
            </div>
          </div>          {/* QR Code d'authentification (aligné à droite) */}
          <div className="w-1/3 flex justify-end">
            <div className="text-right flex flex-col items-end">
              <QRCodeSVG 
                value={qrValue}
                size={60}
                level="H"
              />
              <p className="text-xs text-gray-500 mt-1">Authentification</p>
            </div>
          </div>
        </div>
          {/* Titre et exercice juste en dessous */}
        <div className="mt-3 mb-3 text-center border-t border-gray-200 pt-2">
          <h2 className="text-lg font-bold">{title}</h2>
          <p className="text-sm">Exercice clos le 31 décembre {date}</p>
          <p className="text-xs text-gray-500 italic">Norme comptable : SYSCOHADA — Acte Uniforme OHADA</p>
          <p className="text-xs text-gray-500">Monnaie de présentation : {currency === 'USD' ? 'Dollars américains (USD)' : 'Francs congolais (CDF)'}</p>
          <div className="flex items-center justify-center gap-1 mt-1">
            {isAudited ? (
              <span className="text-xs font-medium text-green-700">✓ État audité</span>
            ) : (
              <span className="text-xs font-medium text-amber-600">⚠ État provisoire — non audité</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">Document établi le {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} par {generatedBy}</p>
        </div>
      </div>

      {/* Contenu du rapport selon le type */}
      <div className="mb-8">
        {type === 'balance' && renderBalanceSheet()}
        {type === 'income' && renderIncomeStatement()}
        {type === 'cashflow' && renderCashFlow()}
        {type === 'trial-balance' && renderTrialBalance()}
        {type === 'general-ledger' && renderGenericTable()}
        {type === 'journal-book' && renderGenericTable()}
        {!['balance', 'income', 'cashflow', 'trial-balance', 'general-ledger', 'journal-book'].includes(type) && (
          <div className="text-center py-4 text-gray-500">Type de rapport non supporté pour l&apos;impression : {type}</div>
        )}
      </div>      {/* Pied de page */}
      <div className="mt-8 pt-4 border-t border-gray-300 text-gray-600 text-sm">
        <div className="flex justify-between items-center">
          <p>Document certifié conforme aux données comptables</p>
          <p>{organization.name} &copy; {new Date().getFullYear()}</p>
        </div>
        <div className="text-center text-xs text-gray-500 mt-2">
          &copy; Produit par Wanzo, développé par i-kiotahub
        </div>
      </div>
    </div>
  );
}
